/**
 * Bounded pagination for the EDDM carrier-route lookup.
 *
 * Clicking a distribution location fires a paginated walk over pathfinder's
 * carrier routes (500 at a time). Before this module the walk had no deadline,
 * no page cap and no way to tell "failed" from "empty": a stalled request left
 * the map's `loading` flag true forever and the user sat on a spinner reading
 * "Loading distribution areas" with no error and no retry.
 *
 * The walk is deliberately pure and dependency-free (no React, no store, no
 * GraphQL) so both the desktop (SegmentsV3) and mobile (AudienceMapEDDM) paths
 * share one implementation, and so it can be executed by
 * `scripts/test-eddm-route-walk.mjs` — this repo's jest is broken repo-wide
 * (babel loose-mode error in src/setupTests.js), so a plain-node harness is the
 * only runner that can actually exercise this logic.
 */

/**
 * Hard cap on pages walked before we stop and tell the user we truncated.
 *
 * Production had no cap at all, so any cap is a potential regression for a very
 * dense area. Sizing measurement (see DEFAULT_WALK_TIMEOUT_MS below): a
 * dense-metro lookup at a 10-mile radius matched 4,369 routes -> 9 pages at
 * pathfinder's 500/page. 40 pages (20,000 routes) is >4x that, which keeps the
 * cap off the path of every realistic lookup while still bounding a runaway
 * cursor. The wall-clock budget, not this, is the binding constraint.
 */
export const DEFAULT_MAX_PAGES = 40;

/**
 * Wall-clock budget for the whole walk.
 *
 * This is here to turn "forever" into "bounded", NOT to cut off a slow-but-
 * working request: a lookup that succeeds slowly today must still succeed.
 *
 * Sizing evidence (the only measurement obtainable from this environment —
 * there is no prod/UAT access and the local pathfinder DB has no carrier-route
 * data, so this reproduces pathfinder's exact query shape against a synthetic
 * 180,000-document collection, matching the production collection's order of
 * magnitude, seeded with a dense NYC cluster):
 *
 *   matching routes in a 10-mile radius = 4,369  ->  9 pages at 500/page
 *   per page: countDocuments 23-25ms, find().skip(n).limit(500) 21ms -> 41ms
 *             (find cost grows with skip, as the reviewer noted)
 *   total DB time for the whole 9-page walk = 497ms
 *
 * So the database is NOT where the field-reported 80-120s goes; at ~55ms/page
 * of DB work the remainder is payload transfer/parse of the route polygons and
 * per-request latency. That means the walk budget cannot be derived from a
 * server-side number — it has to clear the worst wall-clock the field actually
 * reports. 300s is ~2.5x the 120s worst case reported by the users who filed
 * this complaint, so nobody who succeeds today starts failing; SLOW_WALK_NOTICE_MS
 * tells them it is taking longer than usual after 10s either way.
 *
 * Revisit (down to ~60s) once pathfinder stops re-running countDocuments per
 * page over a $near sort.
 */
export const DEFAULT_WALK_TIMEOUT_MS = 300000;

/** After this long the loader admits it is taking longer than usual. */
export const SLOW_WALK_NOTICE_MS = 10000;

export const ROUTE_WALK_TIMEOUT_NAME = "RouteWalkTimeoutError";

export const createRouteWalkTimeoutError = (ms) => {
  const error = new Error(`Carrier-route lookup timed out after ${ms}ms`);
  error.name = ROUTE_WALK_TIMEOUT_NAME;
  return error;
};

/**
 * A cancel is a separate outcome from a timeout.
 *
 * With a 300s budget the worst case is five minutes on a spinner, so the user
 * needs a way out that is not a page reload. That exit must not be dressed up
 * as a failure: the user knows what they did, and "it timed out" / "check your
 * connection" would both be lies.
 */
export const ROUTE_WALK_CANCELLED_NAME = "RouteWalkCancelledError";

export const createRouteWalkCancelledError = () => {
  const error = new Error("Carrier-route lookup was cancelled");
  error.name = ROUTE_WALK_CANCELLED_NAME;
  return error;
};

const isAborted = (signal) => Boolean(signal && signal.aborted);

/**
 * Percentage of the walk completed, or null when it cannot be known.
 *
 * The old inline version divided by a possibly-undefined totalCount and
 * rendered the literal string "NaN %"; it also measured the routes loaded
 * BEFORE the current page merged, so it lagged a full page behind.
 */
export const computeLoadingPercentage = (loadedCount, totalCount) => {
  if (!Number.isFinite(loadedCount) || !Number.isFinite(totalCount) || totalCount <= 0) {
    return null;
  }
  const percentage = (loadedCount * 100) / totalCount;
  if (!Number.isFinite(percentage)) {
    return null;
  }
  return Math.min(100, Math.max(0, percentage)).toFixed();
};

/**
 * Reject with a typed timeout error if `promise` has not settled within `ms`,
 * or with a typed cancel error as soon as `signal` aborts.
 *
 * The cancel arm is what makes Cancel feel instant: without it the user would
 * press Cancel and still wait for the current page's request to come back
 * before the map became usable again.
 */
export const withDeadline = (promise, ms, signal) =>
  new Promise((resolve, reject) => {
    let settled = false;
    let abortListener = null;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (signal && abortListener) signal.removeEventListener("abort", abortListener);
      fn(value);
    };

    const timer = setTimeout(() => finish(reject, createRouteWalkTimeoutError(ms)), ms);

    if (signal) {
      abortListener = () => finish(reject, createRouteWalkCancelledError());
      if (signal.aborted) {
        abortListener();
        return;
      }
      signal.addEventListener("abort", abortListener);
    }

    Promise.resolve(promise).then(
      (value) => finish(resolve, value),
      (error) => finish(reject, error)
    );
  });

/**
 * Walk every page of carrier routes for one location.
 *
 * @param {object} params
 * @param {(args: {after?: string, accumulatedSuggestions: Array}) => Promise<object>} params.fetchPage
 *   resolves the discriminated page result from `getCarrierRoutesWithSuggestions`.
 *   `accumulatedSuggestions` MUST be forwarded to the server: pathfinder builds the
 *   global top-5 by concatenating it with the current page's scored routes
 *   (oppizi-pathfinder/src/db/usCarrierRoutes.js), so dropping it would silently
 *   reduce `suggestions` to the top-5 of the last page only.
 *   ({ ok: true, data, pageInfo, totalCount, suggestions } | { ok: false, error }).
 * @param {(progress: object) => void} [params.onProgress] called after each page merges.
 * @param {AbortSignal} [params.signal] aborting it ends the walk with
 *   `reason: "cancelled"`. It is forwarded to `fetchPage` so the in-flight HTTP
 *   request is aborted too, not merely abandoned.
 * @param {number} [params.maxPages]
 * @param {number} [params.timeoutMs] wall-clock budget for the entire walk.
 * @param {() => number} [params.now] injectable clock.
 * @returns {Promise<object>} `{ ok: true, routes, suggestions, totalCount, pagesFetched, truncated }`
 *   or `{ ok: false, reason: "timeout" | "request" | "cancelled", error, routes, pagesFetched }`.
 *   `routes` is always present so a partial result can still be drawn.
 */
export const walkCarrierRoutePages = async ({
  fetchPage,
  onProgress,
  signal,
  maxPages = DEFAULT_MAX_PAGES,
  timeoutMs = DEFAULT_WALK_TIMEOUT_MS,
  now = () => Date.now(),
}) => {
  const startedAt = now();
  let routes = {};
  let suggestions = [];
  let totalCount;
  let pagesFetched = 0;
  let truncated = null;
  let after;

  const cancelled = () => ({
    ok: false,
    reason: "cancelled",
    error: createRouteWalkCancelledError(),
    // Whatever already arrived stays available, so cancelling leaves the user
    // with a partial map rather than a blank one.
    routes,
    pagesFetched,
  });

  for (;;) {
    if (isAborted(signal)) {
      return cancelled();
    }

    const remainingMs = timeoutMs - (now() - startedAt);
    if (remainingMs <= 0) {
      return {
        ok: false,
        reason: "timeout",
        error: createRouteWalkTimeoutError(timeoutMs),
        routes,
        pagesFetched,
      };
    }

    let page;
    try {
      page = await withDeadline(fetchPage({ after, accumulatedSuggestions: suggestions, signal }), remainingMs, signal);
    } catch (error) {
      // Order matters: an aborted request rejects with whatever the transport
      // produced, which must still be reported as a cancel and not as a
      // timeout or a network failure.
      if (isAborted(signal) || (error && error.name === ROUTE_WALK_CANCELLED_NAME)) {
        return cancelled();
      }
      return {
        ok: false,
        reason: error && error.name === ROUTE_WALK_TIMEOUT_NAME ? "timeout" : "request",
        error,
        routes,
        pagesFetched,
      };
    }

    if (isAborted(signal)) {
      return cancelled();
    }

    if (!page || page.ok === false) {
      return {
        ok: false,
        reason: "request",
        error: (page && page.error) || new Error("Carrier-route lookup failed"),
        routes,
        pagesFetched,
      };
    }

    pagesFetched += 1;
    routes = { ...routes, ...(page.data || {}) };
    if (Array.isArray(page.suggestions)) {
      suggestions = page.suggestions;
    }
    if (Number.isFinite(page.totalCount)) {
      totalCount = page.totalCount;
    }

    const loadedCount = Object.keys(routes).length;
    if (onProgress) {
      onProgress({
        loadedCount,
        totalCount,
        pagesFetched,
        elapsedMs: now() - startedAt,
        // Computed AFTER the merge, so it reflects what the user can already see.
        percentage: computeLoadingPercentage(loadedCount, totalCount),
      });
    }

    if (!page.pageInfo || !page.pageInfo.hasNextPage) {
      break;
    }
    if (pagesFetched >= maxPages) {
      truncated = "pageLimit";
      break;
    }
    after = page.pageInfo.endCursor;
  }

  return { ok: true, routes, suggestions, totalCount, pagesFetched, truncated };
};

/** User-facing copy for a walk that did not complete, keyed by why. */
export const ROUTE_LOAD_ERROR_MESSAGES = {
  timeout: "Loading distribution areas is taking longer than expected.",
  request: "Couldn't load distribution areas.",
  cancelled: "Loading distribution areas was stopped.",
};

/**
 * Second line under the message. A cancel is not a fault, so it must not be
 * told to go check its connection.
 */
export const ROUTE_LOAD_ERROR_DETAILS = {
  timeout: "Check your connection and try again.",
  request: "Check your connection and try again.",
  cancelled: "You stopped this search. Try again, or pick a smaller radius to make it quicker.",
};

/** @returns {string|null} the message to show, or null when the walk succeeded. */
export const routeWalkErrorMessage = (result) => {
  if (!result || result.ok !== false) {
    return null;
  }
  return ROUTE_LOAD_ERROR_MESSAGES[result.reason] || ROUTE_LOAD_ERROR_MESSAGES.request;
};

/** @returns {string|null} the supporting line for {@link routeWalkErrorMessage}. */
export const routeWalkErrorDetail = (result) => {
  if (!result || result.ok !== false) {
    return null;
  }
  return ROUTE_LOAD_ERROR_DETAILS[result.reason] || ROUTE_LOAD_ERROR_DETAILS.request;
};

/**
 * Non-blocking notice for a walk that succeeded but stopped at the page cap —
 * the user is looking at a partial map and must be told, not left guessing.
 * @returns {string|null}
 */
export const routeWalkNotice = (result) => {
  if (!result || result.ok !== true || result.truncated !== "pageLimit") {
    return null;
  }
  const loaded = Object.keys(result.routes || {}).length;
  return `Showing the first ${loaded} distribution areas. Reduce the radius to load a more precise area.`;
};
